import { z } from "zod";

import { DAYS_OF_WEEK } from "@/shared/domain/metrics/MetricRecord";

import { boardTimeZoneSchema, weekdayNumber } from "./boardCalendar";
import {
  INTERACTION_TIME_UNITS,
  type SerializedInteractionValue,
} from "./interactionTypes";
import {
  compareInteractionValues,
  convertInteractionValue,
  integerStringSchema,
  interactionValueTypeSchema,
  parseInteractionValue,
  serializedInteractionValueSchema,
  serializeInteractionValue,
  type ValueResult,
} from "./interactionValueCodec";
import {
  MARK_LINEAGE_AGGREGATES,
  MARK_LINEAGE_CALENDAR_UNITS,
  MARK_LINEAGE_ENCODINGS,
  MARK_LINEAGE_GRAINS,
  MARK_ROLES,
  MARK_TYPES,
  type CalendarComponents,
  type CalendarGrouping,
  type GroupDimensionValue,
  type GroupDimensionMapping,
  type ExactValue,
  type MarkLineageCalendarUnit,
  type SerializedExactValue,
} from "./markLineageTypes";

const name = z.string().min(1);
/** Precision tops out at 76 digits, Arrow's Decimal256 limit. */
const decimalShape = {
  scale: z.number().int().min(-76).max(76),
  precision: z.number().int().min(1).max(76),
  bitWidth: z.union([z.literal(128), z.literal(256)]),
};
/** The most decimal digits an Arrow Decimal128 holds. */
export const DECIMAL128_MAX_PRECISION = 38;
/** The value types a bin dimension and a bin grid's origin accept. */
const BINNABLE_VALUE_TYPES: readonly string[] = [
  "number",
  "integer",
  "instant",
];
/** The components a calendar group of each unit carries. */
const CALENDAR_COMPONENTS_BY_UNIT: Record<
  MarkLineageCalendarUnit,
  readonly (keyof CalendarComponents)[]
> = {
  yearmonthdate: ["year", "month", "date"],
  yearmonth: ["year", "month"],
  hours: ["hours"],
  day: ["day"],
  yearweek: ["year", "month", "date"],
};
/** The mapping kinds that can carry a dimension of each transform. */
const MAPPING_KINDS_BY_TRANSFORM: Record<
  "identity" | "bin" | "calendar",
  readonly GroupDimensionMapping["kind"][]
> = {
  identity: ["identity"],
  bin: ["bin-source", "bin-output"],
  calendar: ["calendar-source", "calendar-output"],
};

/** Whether both values parse and `lower` sorts before `upper`; values of types that cannot be compared fail. */
function isStrictlyBelow(
  lower: SerializedInteractionValue,
  upper: SerializedInteractionValue,
): boolean {
  const lowerValue = parseInteractionValue(lower);
  const upperValue = parseInteractionValue(upper);
  if (lowerValue.status !== "valid" || upperValue.status !== "valid") {
    return false;
  }
  const order = compareInteractionValues(lowerValue.value, upperValue.value);
  return order.status === "valid" && order.value < 0;
}

export const exactValueTypeSchema = z.union([
  interactionValueTypeSchema,
  z.strictObject({
    type: z.literal("decimal"),
    ...decimalShape,
    nullable: z.boolean().optional(),
  }),
]);
const fieldRefSchema = z.strictObject({
  bindingName: name,
  field: name,
});
const lineageFieldRefSchema = z.strictObject({
  lineage: name,
  field: name,
});
const calendarGroupingShape = {
  unit: z.enum(MARK_LINEAGE_CALENDAR_UNITS),
  epoch: name,
};
const calendarGroupingSchema = z.strictObject(calendarGroupingShape);
const zonedCalendarGroupingSchema = z.strictObject({
  ...calendarGroupingShape,
  timeZone: boardTimeZoneSchema,
  weekStart: z.enum(DAYS_OF_WEEK),
});
export const binGridSchema = z
  .strictObject({
    origin: serializedInteractionValueSchema,
    unit: serializedInteractionValueSchema,
  })
  .superRefine((binGrid, ctx) => {
    const origin = parseInteractionValue(binGrid.origin);
    const unit = parseInteractionValue(binGrid.unit);
    if (origin.status !== "valid" || unit.status !== "valid") {
      return;
    }
    const unitIsPositive =
      (unit.value.type === "number" && unit.value.value > 0) ||
      (unit.value.type === "integer" && unit.value.value > 0n);
    if (!unitIsPositive || !BINNABLE_VALUE_TYPES.includes(origin.value.type)) {
      ctx.addIssue({
        code: "custom",
        message:
          "A bin grid needs a number, integer or instant origin and a positive number or integer unit.",
      });
    }
  });
export const binParametersSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("automatic") }),
    z.strictObject({
      kind: z.literal("maxbins"),
      maxbins: z.number().int().positive(),
    }),
    z.strictObject({
      kind: z.literal("step"),
      step: z.number().finite().positive(),
      extent: z.tuple([z.number().finite(), z.number().finite()]).optional(),
      anchor: z.number().finite().optional(),
    }),
    z.strictObject({
      kind: z.literal("edges"),
      edges: z.array(serializedInteractionValueSchema).min(2),
    }),
  ])
  .superRefine((bin, ctx) => {
    if (bin.kind === "step" && bin.extent && bin.extent[0] >= bin.extent[1]) {
      ctx.addIssue({
        code: "custom",
        path: ["extent"],
        message: "Bin extent must increase.",
      });
    }
    if (bin.kind !== "edges") {
      return;
    }
    for (let index = 1; index < bin.edges.length; index++) {
      if (!isStrictlyBelow(bin.edges[index - 1], bin.edges[index])) {
        ctx.addIssue({
          code: "custom",
          path: ["edges", index],
          message:
            "Bin edges must have comparable types and strictly increase.",
        });
      }
    }
  });
const markLineageDimensionShape = {
  id: name,
  source: fieldRefSchema,
  valueType: interactionValueTypeSchema,
  output: lineageFieldRefSchema,
  encoding: z.enum(MARK_LINEAGE_ENCODINGS).optional(),
};
const markLineageDimensionSchema = z.discriminatedUnion("kind", [
  z.strictObject({ ...markLineageDimensionShape, kind: z.literal("identity") }),
  z.strictObject({
    ...markLineageDimensionShape,
    kind: z.literal("bin"),
    parameters: binParametersSchema,
    upper: lineageFieldRefSchema,
    binGrid: binGridSchema.optional(),
  }),
  z.strictObject({
    ...markLineageDimensionShape,
    kind: z.literal("calendar"),
    calendar: calendarGroupingSchema,
  }),
]);
const markLineageMeasureSchema = z.strictObject({
  id: name,
  source: fieldRefSchema.optional(),
  output: lineageFieldRefSchema,
  sourceType: exactValueTypeSchema.optional(),
  encoding: z.enum(MARK_LINEAGE_ENCODINGS).optional(),
  valueType: exactValueTypeSchema,
  aggregate: z.enum(MARK_LINEAGE_AGGREGATES).optional(),
  role: z.enum(["measure", "reference"]),
  additive: z.boolean(),
});
const markLineageTransformSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("aggregate"),
    dimensions: z.array(name),
    measures: z.array(name).min(1),
  }),
  z.strictObject({ kind: z.literal("bin"), dimension: name }),
  z.strictObject({ kind: z.literal("calendar"), dimension: name }),
  z.strictObject({
    kind: z.literal("stack"),
    measure: name,
    groupBy: z.array(name),
    series: z.array(name),
    offset: z.enum(["zero", "normalize", "center"]),
    start: lineageFieldRefSchema,
    end: lineageFieldRefSchema,
  }),
  z.strictObject({
    kind: z.literal("fold"),
    fields: z.array(fieldRefSchema).min(1),
    key: lineageFieldRefSchema,
    value: lineageFieldRefSchema,
  }),
  z.strictObject({
    kind: z.literal("boxplot"),
    source: fieldRefSchema,
    groupBy: z.array(name),
    extent: z.discriminatedUnion("kind", [
      z.strictObject({
        kind: z.literal("iqr"),
        multiplier: z.number().finite().nonnegative(),
      }),
      z.strictObject({ kind: z.literal("min-max") }),
    ]),
    outputs: z.strictObject({
      lower: lineageFieldRefSchema,
      q1: lineageFieldRefSchema,
      median: lineageFieldRefSchema,
      q3: lineageFieldRefSchema,
      upper: lineageFieldRefSchema,
    }),
  }),
]);
/** A `TemporalDuration`: a whole number of a unit, the ticks written as a
 *  decimal string so a nanosecond count past 2^53 survives JSON. */
export const temporalDurationSchema = z.strictObject({
  ticks: z.string().regex(/^(?:0|[1-9]\d*)$/),
  unit: z.enum(INTERACTION_TIME_UNITS),
});
export const temporalMatchPolicySchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("exact") }),
  z.strictObject({
    kind: z.literal("nearest"),
    tolerance: temporalDurationSchema,
  }),
]);
export const markLineageSchema = z.strictObject({
  id: name,
  bindingName: name,
  unitViewPath: z.array(z.union([name, z.number().int().nonnegative()])),
  markType: z.enum(MARK_TYPES),
  grain: z.enum(MARK_LINEAGE_GRAINS),
  markRoles: z.array(z.enum(MARK_ROLES)).min(1),
  dimensions: z.array(markLineageDimensionSchema),
  series: z.array(name),
  measures: z.array(markLineageMeasureSchema),
  transforms: z.array(markLineageTransformSchema),
  coordinate: z
    .strictObject({
      source: fieldRefSchema,
      output: lineageFieldRefSchema,
      valueType: interactionValueTypeSchema,
      encoding: z.enum(MARK_LINEAGE_ENCODINGS).optional(),
      matching: temporalMatchPolicySchema,
      comparableSources: z
        .strictObject({
          epoch: name,
          fields: z.array(fieldRefSchema).min(1),
        })
        .optional(),
    })
    .optional(),
  total: z
    .strictObject({ measures: z.array(name).min(1), label: name.optional() })
    .optional(),
});
export const groupDimensionMappingSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    dimension: name,
    valueType: interactionValueTypeSchema,
    kind: z.literal("fold-output"),
    field: name,
    fields: z.array(fieldRefSchema).min(1),
  }),
  z.strictObject({
    dimension: name,
    valueType: interactionValueTypeSchema,
    kind: z.literal("fold-source"),
    sources: z
      .array(z.strictObject({ source: fieldRefSchema, field: name }))
      .min(1),
  }),
  z.strictObject({
    dimension: name,
    field: name,
    valueType: interactionValueTypeSchema,
    kind: z.literal("identity"),
  }),
  z.strictObject({
    dimension: name,
    field: name,
    valueType: interactionValueTypeSchema,
    kind: z.literal("bin-source"),
    binGrid: binGridSchema.optional(),
  }),
  z.strictObject({
    dimension: name,
    field: name,
    valueType: interactionValueTypeSchema,
    kind: z.literal("bin-output"),
    upperField: name,
    binGrid: binGridSchema.optional(),
  }),
  z.strictObject({
    dimension: name,
    field: name,
    valueType: interactionValueTypeSchema,
    kind: z.literal("calendar-source"),
    calendar: calendarGroupingSchema,
  }),
  z.strictObject({
    dimension: name,
    field: name,
    valueType: interactionValueTypeSchema,
    kind: z.literal("calendar-output"),
    calendar: calendarGroupingSchema,
  }),
]);
const groupDimensionValueShape = {
  id: name,
  source: fieldRefSchema,
  valueType: interactionValueTypeSchema,
};
const calendarComponentsSchema = z.strictObject({
  year: z.number().int().optional(),
  month: z.number().int().min(1).max(12).optional(),
  date: z.number().int().min(1).max(31).optional(),
  hours: z.number().int().min(0).max(23).optional(),
  day: z.number().int().min(0).max(6).optional(),
});
export const groupDimensionValueSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({
      ...groupDimensionValueShape,
      kind: z.literal("fold"),
      fields: z.array(fieldRefSchema).min(1),
    }),
    z.strictObject({
      ...groupDimensionValueShape,
      kind: z.literal("null"),
      transform: z.enum(["identity", "bin", "calendar"]),
      calendar: calendarGroupingSchema.optional(),
      binGrid: binGridSchema.optional(),
    }),
    z.strictObject({
      ...groupDimensionValueShape,
      kind: z.literal("identity"),
      value: serializedInteractionValueSchema,
    }),
    z.strictObject({
      ...groupDimensionValueShape,
      kind: z.literal("bin"),
      lower: serializedInteractionValueSchema,
      upper: serializedInteractionValueSchema,
      lowerInclusive: z.boolean(),
      upperInclusive: z.boolean(),
      binGrid: binGridSchema.optional(),
    }),
    z.strictObject({
      ...groupDimensionValueShape,
      kind: z.literal("calendar"),
      calendar: zonedCalendarGroupingSchema,
      components: calendarComponentsSchema,
    }),
  ])
  .superRefine((dimension, ctx) => {
    if (dimension.kind === "fold") {
      const fields = dimension.fields.map((field) => JSON.stringify(field));
      if (
        dimension.valueType.type !== "string" ||
        dimension.valueType.nullable ||
        new Set(fields).size !== fields.length ||
        !fields.includes(JSON.stringify(dimension.source))
      ) {
        ctx.addIssue({
          code: "custom",
          message:
            "A fold dimension names one of its declared source fields and has a non-null string value type.",
        });
      }
      return;
    }
    const transform =
      dimension.kind === "null" ? dimension.transform : dimension.kind;
    if (
      transform === "bin" &&
      !BINNABLE_VALUE_TYPES.includes(dimension.valueType.type)
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "A bin dimension needs a number, integer or instant value type.",
      });
    }
    if (
      "calendar" in dimension &&
      dimension.calendar !== undefined &&
      (dimension.valueType.type !== "instant" ||
        dimension.valueType.epoch !== dimension.calendar.epoch)
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "A calendar dimension needs an instant value type with the calendar grouping's epoch.",
      });
    }
    if (dimension.kind === "null") {
      if (
        (dimension.transform === "calendar") !==
          (dimension.calendar !== undefined) ||
        (dimension.transform !== "bin" && dimension.binGrid !== undefined)
      ) {
        ctx.addIssue({
          code: "custom",
          message:
            "A null dimension carries `calendar` exactly when its transform is calendar, and `binGrid` only when it is bin.",
        });
      }
      if (!dimension.valueType.nullable) {
        ctx.addIssue({
          code: "custom",
          message: "A null dimension needs a nullable value type.",
        });
      }
      return;
    }
    if (dimension.kind === "calendar") {
      const required = CALENDAR_COMPONENTS_BY_UNIT[dimension.calendar.unit];
      if (
        Object.keys(dimension.components).length !== required.length ||
        required.some((key) => !(key in dimension.components))
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["components"],
          message:
            "Calendar components must describe exactly the declared grouping unit.",
        });
      }
      const { year, month, date } = dimension.components;
      if (year !== undefined && month !== undefined && date !== undefined) {
        // Date rolls a nonexistent date such as February 31 into March, so it reads back changed.
        const day = new Date(0);
        day.setUTCFullYear(year, month - 1, date);
        if (
          day.getUTCFullYear() !== year ||
          day.getUTCMonth() !== month - 1 ||
          day.getUTCDate() !== date ||
          (dimension.calendar.unit === "yearweek" &&
            day.getUTCDay() !== weekdayNumber(dimension.calendar.weekStart))
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["components"],
            message:
              "Use a real calendar date; for a week, use its first day, which falls on the board's week start day.",
          });
        }
      }
      return;
    }
    const values =
      dimension.kind === "bin"
        ? [dimension.lower, dimension.upper]
        : [dimension.value];
    if (values.some((value) => value.type === "null")) {
      ctx.addIssue({
        code: "custom",
        message: 'Write a null value as a dimension with kind "null".',
      });
    }
    for (const value of values) {
      const parsed = parseInteractionValue(value);
      if (
        parsed.status !== "valid" ||
        convertInteractionValue(parsed.value, dimension.valueType).status !==
          "valid"
      ) {
        ctx.addIssue({
          code: "custom",
          message:
            "Dimension values must convert to the dimension's value type.",
        });
      }
    }
    if (
      dimension.kind === "bin" &&
      !isStrictlyBelow(dimension.lower, dimension.upper)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "A bin's lower bound must be below its upper bound.",
      });
    }
  });
export const groupClauseSchema = z
  .strictObject({
    kind: z.literal("group"),
    markLineageId: name,
    groups: z.array(z.array(groupDimensionValueSchema).min(1)),
  })
  .superRefine((clause, ctx) => {
    if (clause.groups.length === 0) {
      return;
    }
    // A group's dimensions with their values left out. Every group of one clause must give the
    // first group's key: the same dimensions, with the same sources, types and groupings.
    const dimensionSetKey = (dimensions: GroupDimensionValue[]) =>
      JSON.stringify(
        dimensions
          .map((dimension) => ({
            id: dimension.id,
            source: dimension.kind === "fold" ? undefined : dimension.source,
            fields:
              dimension.kind === "fold"
                ? dimension.fields.map((field) => JSON.stringify(field)).sort()
                : undefined,
            valueType: dimension.valueType,
            kind:
              dimension.kind === "null" ? dimension.transform : dimension.kind,
            calendar: "calendar" in dimension ? dimension.calendar : undefined,
            binGrid: "binGrid" in dimension ? dimension.binGrid : undefined,
          }))
          .sort((left, right) => (left.id < right.id ? -1 : 1)),
      );
    const firstGroupKey = dimensionSetKey(clause.groups[0]);
    clause.groups.forEach((dimensions, index) => {
      if (
        new Set(dimensions.map(({ id }) => id)).size !== dimensions.length ||
        dimensionSetKey(dimensions) !== firstGroupKey
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["groups", index],
          message:
            "Every selected group must include the same complete set of dimensions once.",
        });
      }
    });
  });
const exactDecimalSchema = z
  .strictObject({
    type: z.literal("decimal"),
    coefficient: integerStringSchema,
    ...decimalShape,
  })
  .superRefine((value, ctx) => {
    if (
      (value.bitWidth === 128 && value.precision > DECIMAL128_MAX_PRECISION) ||
      value.coefficient.replace("-", "").length > value.precision
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "The Decimal coefficient must fit its declared precision and bit width.",
      });
    }
  });
/** Throws when a Decimal's coefficient has more digits than its precision or bit width allows. */
export function serializeExactValue(value: ExactValue): SerializedExactValue {
  if (value.type !== "decimal") {
    return serializeInteractionValue(value);
  }
  return exactDecimalSchema.parse({
    ...value,
    coefficient: value.coefficient.toString(),
  });
}
/** Writes the Decimal with `scale` digits after the point, trailing zeros included; a negative scale appends zeros. */
export function formatExactDecimal(
  value: Extract<ExactValue, { type: "decimal" }>,
): string {
  const negative = value.coefficient < 0n;
  const sign = negative ? "-" : "";
  const digits = (negative ? -value.coefficient : value.coefficient).toString();
  if (value.scale <= 0) {
    return `${sign}${digits}${"0".repeat(-value.scale)}`;
  }
  const padded = digits.padStart(value.scale + 1, "0");
  return `${sign}${padded.slice(0, -value.scale)}.${padded.slice(-value.scale)}`;
}
/**
 * Two groups get the same key exactly when they hold the same dimension values. Dimension order, fold field order,
 * instant provenance and how a value is written (a number or an integer, ticks in one unit or another) do not change
 * the key. Throws when the dimensions do not form a valid group.
 */
export function groupDimensionsKey(dimensions: GroupDimensionValue[]): string {
  // Parsing writes every object's keys in schema order, so equal groups stringify alike.
  const parsed = groupClauseSchema.parse({
    kind: "group",
    markLineageId: "key",
    groups: [dimensions],
  });
  return JSON.stringify(
    parsed.groups[0]
      .map((dimension) => {
        if (dimension.kind === "fold") {
          return {
            ...dimension,
            fields: [...dimension.fields].sort((a, b) =>
              JSON.stringify(a).localeCompare(JSON.stringify(b)),
            ),
          };
        }
        if (dimension.kind !== "identity" && dimension.kind !== "bin") {
          return dimension;
        }
        const normalize = (value: SerializedInteractionValue) => {
          const parsedValue = parseInteractionValue(value);
          if (parsedValue.status !== "valid") {
            throw new Error(parsedValue.message);
          }
          const converted = convertInteractionValue(
            parsedValue.value,
            dimension.valueType,
          );
          if (converted.status !== "valid") {
            throw new Error(converted.message);
          }
          if (converted.value.type === "instant") {
            const { provenance: _provenance, ...instant } = converted.value;
            return serializeInteractionValue(instant);
          }
          return serializeInteractionValue(converted.value);
        };
        if (dimension.kind === "identity") {
          return { ...dimension, value: normalize(dimension.value) };
        }
        return {
          ...dimension,
          lower: normalize(dimension.lower),
          upper: normalize(dimension.upper),
        };
      })
      .sort((left, right) => left.id.localeCompare(right.id)),
  );
}

const groupDimensionMappingsSchema = z.array(groupDimensionMappingSchema);

/**
 * Checks that the mappings carry each dimension of a rendered group exactly once, onto a field of a compatible type
 * whose kind fits the dimension's transform and that has the dimension's calendar grouping, bin grid, or folded
 * source fields.
 */
export function validateGroupMappings(
  dimensions: GroupDimensionValue[],
  mappings: GroupDimensionMapping[],
): ValueResult<GroupDimensionMapping[]> {
  return groupMappingsCheck(mappings)(dimensions);
}

/**
 * `validateGroupMappings` with the mappings fixed, for checking every rendered row of a chart against them. The
 * mappings are parsed once, and the answer for each group, told apart by its dimensions' JSON text, is kept, since the
 * rows of one series share their group.
 */
export function groupMappingsCheck(
  mappings: GroupDimensionMapping[],
): (dimensions: GroupDimensionValue[]) => ValueResult<GroupDimensionMapping[]> {
  const parsed = groupDimensionMappingsSchema.safeParse(mappings);
  const results = new Map<string, ValueResult<GroupDimensionMapping[]>>();
  return (dimensions) => {
    const key = JSON.stringify(dimensions, (_, value: unknown) =>
      typeof value === "bigint" ? `${value}n` : value,
    );
    let result = results.get(key);
    if (result === undefined) {
      result = checkGroupMappings(dimensions, mappings, parsed);
      results.set(key, result);
    }
    return result;
  };
}

/** Whether a dimension's calendar grouping groups by the same unit over the
 *  same epoch as a mapping's, whatever zone and week start it was cut in. */
function sameCalendarGrouping(
  dimensionCalendar: CalendarGrouping | undefined,
  mappingCalendar: CalendarGrouping,
): boolean {
  return (
    dimensionCalendar?.unit === mappingCalendar.unit &&
    dimensionCalendar.epoch === mappingCalendar.epoch
  );
}

function checkGroupMappings(
  dimensions: GroupDimensionValue[],
  mappings: GroupDimensionMapping[],
  parsed: ReturnType<typeof groupDimensionMappingsSchema.safeParse>,
): ValueResult<GroupDimensionMapping[]> {
  const parsedDimensions = z
    .array(groupDimensionValueSchema)
    .safeParse(dimensions);
  if (!parsed.success || !parsedDimensions.success) {
    const issues = [
      ...(parsedDimensions.error?.issues ?? []).map(
        (issue) => `Group dimension ${issue.path.join(".")}: ${issue.message}`,
      ),
      ...(parsed.error?.issues ?? []).map(
        (issue) => `Mapping ${issue.path.join(".")}: ${issue.message}`,
      ),
    ];
    return { status: "invalid", message: issues.join(" ") };
  }
  if (
    new Set(dimensions.map(({ id }) => id)).size !== dimensions.length ||
    new Set(mappings.map(({ dimension }) => dimension)).size !==
      dimensions.length ||
    mappings.length !== dimensions.length
  ) {
    return {
      status: "invalid",
      message: "Map every dimension of the rendered group exactly once.",
    };
  }
  for (const dimension of parsedDimensions.data) {
    const mapping = parsed.data.find(
      (entry) => entry.dimension === dimension.id,
    );
    if (
      !mapping ||
      mapping.valueType.type !== dimension.valueType.type ||
      (dimension.valueType.nullable && !mapping.valueType.nullable)
    ) {
      return {
        status: "invalid",
        message: `Dimension "${dimension.id}" has no compatible target type.`,
      };
    }
    if (
      dimension.valueType.type === "instant" &&
      mapping.valueType.type === "instant" &&
      dimension.valueType.epoch !== mapping.valueType.epoch
    ) {
      return {
        status: "invalid",
        message: "Instant dimensions require the same epoch.",
      };
    }
    if (dimension.kind === "fold") {
      const expected = dimension.fields
        .map((field) => JSON.stringify(field))
        .sort();
      let actual: string[] = [];
      if (mapping.kind === "fold-output") {
        actual = mapping.fields.map((field) => JSON.stringify(field)).sort();
      }
      if (mapping.kind === "fold-source") {
        actual = mapping.sources
          .map(({ source }) => JSON.stringify(source))
          .sort();
      }
      if (
        mapping.valueType.nullable ||
        new Set(actual).size !== actual.length ||
        JSON.stringify(expected) !== JSON.stringify(actual)
      ) {
        return {
          status: "invalid",
          message: "Map every declared folded source field exactly once.",
        };
      }
      continue;
    }
    const transform =
      dimension.kind === "null" ? dimension.transform : dimension.kind;
    if (!MAPPING_KINDS_BY_TRANSFORM[transform].includes(mapping.kind)) {
      return {
        status: "invalid",
        message: `Dimension "${dimension.id}" needs a mapping of its own kind: identity, bin-source or bin-output, or calendar-source or calendar-output.`,
      };
    }
    if (
      (mapping.kind === "calendar-source" ||
        mapping.kind === "calendar-output") &&
      !sameCalendarGrouping(
        "calendar" in dimension ? dimension.calendar : undefined,
        mapping.calendar,
      )
    ) {
      return {
        status: "invalid",
        message:
          "A calendar mapping must use the same unit and epoch as the group the chart draws.",
      };
    }
    if (
      (mapping.kind === "bin-source" || mapping.kind === "bin-output") &&
      JSON.stringify("binGrid" in dimension ? dimension.binGrid : undefined) !==
        JSON.stringify(mapping.binGrid)
    ) {
      return {
        status: "invalid",
        message: "A bin mapping must use the rendered group's bin grid.",
      };
    }
  }
  return { status: "valid", value: parsed.data };
}
